import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "./AuthProvider";
import {
  acceptInvitation,
  addProjectMember,
  archiveProject,
  archiveWorkspace,
  createProject,
  createWorkspace,
  createWorkspaceInvitation,
  declineInvitation,
  deleteWorkspace,
  friendlyMessage,
  getProject,
  getWorkspace,
  listMyInvitations,
  listProjectMembers,
  listProjects,
  listWorkspaceInvitations,
  listWorkspaceMembers,
  listWorkspaces,
  removeProjectMember,
  removeWorkspaceMember,
  revokeWorkspaceInvitation,
  updateProject,
  updateProjectMemberRole,
  updateWorkspace,
  updateWorkspaceMemberRole,
} from "./accountApi";
import type {
  CreateInvitationRequest,
  CreateProjectRequest,
  CreateWorkspaceRequest,
  Project,
  ProjectMember,
  ProjectRole,
  UpdateProjectRequest,
  UpdateWorkspaceRequest,
  Workspace,
  WorkspaceInvitation,
  WorkspaceMember,
  WorkspaceRole,
} from "../models/account";

/** Which nested screen the workspaces section is currently showing. */
export type WorkspacesScreen = "list" | "invitations" | "workspace" | "project";

// Orchestration + data for the workspaces/projects/invitations section: the workspace list, the
// currently drilled-into workspace (with its members/invitations/projects), the currently
// drilled-into project (with its members), and the user's own pending invitations.
export function useWorkspaces() {
  const { status, token } = useAuth();
  const tokenRef = useRef(token);
  tokenRef.current = token;
  const getToken = useMemo(() => () => tokenRef.current(), []);

  const [screen, setScreen] = useState<WorkspacesScreen>("list");
  const [error, setError] = useState<string | null>(null);

  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [loadingWorkspaces, setLoadingWorkspaces] = useState(true);

  const [workspaceId, setWorkspaceId] = useState<string | null>(null);
  const [workspace, setWorkspace] = useState<Workspace | null>(null);
  const [members, setMembers] = useState<WorkspaceMember[]>([]);
  const [invitations, setInvitations] = useState<WorkspaceInvitation[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loadingWorkspace, setLoadingWorkspace] = useState(false);

  const [projectId, setProjectId] = useState<string | null>(null);
  const [project, setProject] = useState<Project | null>(null);
  const [projectMembers, setProjectMembers] = useState<ProjectMember[]>([]);
  const [loadingProject, setLoadingProject] = useState(false);

  const [myInvitations, setMyInvitations] = useState<WorkspaceInvitation[]>([]);
  const [loadingMyInvitations, setLoadingMyInvitations] = useState(false);

  const reloadWorkspaces = useCallback(async () => {
    setLoadingWorkspaces(true);
    setError(null);
    try {
      setWorkspaces(await listWorkspaces(getToken));
    } catch (e) {
      setError(friendlyMessage(e));
    } finally {
      setLoadingWorkspaces(false);
    }
  }, [getToken]);

  useEffect(() => {
    if (status === "authenticated") void reloadWorkspaces();
  }, [status, reloadWorkspaces]);

  async function addWorkspace(req: CreateWorkspaceRequest): Promise<Workspace | null> {
    setError(null);
    try {
      const ws = await createWorkspace(getToken, req);
      await reloadWorkspaces();
      return ws;
    } catch (e) {
      setError(friendlyMessage(e));
      return null;
    }
  }

  const reloadWorkspaceDetail = useCallback(
    async (id: string) => {
      setLoadingWorkspace(true);
      setError(null);
      try {
        const [ws, mem, proj] = await Promise.all([
          getWorkspace(getToken, id),
          listWorkspaceMembers(getToken, id),
          listProjects(getToken, id),
        ]);
        setWorkspace(ws);
        setMembers(mem);
        setProjects(proj);
        // Invitations require workspace-admin; a plain member gets 403. Load them
        // separately so that failure can't tear down the whole detail load (which
        // would otherwise leave the project rail empty for non-admin members).
        try {
          setInvitations(await listWorkspaceInvitations(getToken, id));
        } catch {
          setInvitations([]);
        }
      } catch (e) {
        setError(friendlyMessage(e));
      } finally {
        setLoadingWorkspace(false);
      }
    },
    [getToken],
  );

  function openWorkspace(id: string) {
    setWorkspaceId(id);
    setScreen("workspace");
    void reloadWorkspaceDetail(id);
  }

  function backToList() {
    setScreen("list");
    setWorkspaceId(null);
    setWorkspace(null);
    // Everything below was loaded FOR that workspace. Leaving it behind kept the deleted (or merely
    // exited) workspace's projects on the rail, which then navigated into rows the server no longer has.
    setMembers([]);
    setInvitations([]);
    setProjects([]);
    setProjectId(null);
    setProject(null);
    setProjectMembers([]);
  }

  function showMyInvitations() {
    setScreen("invitations");
    void reloadMyInvitations();
  }

  async function saveWorkspace(req: UpdateWorkspaceRequest) {
    if (!workspaceId) return;
    setError(null);
    try {
      setWorkspace(await updateWorkspace(getToken, workspaceId, req));
      await reloadWorkspaces();
    } catch (e) {
      setError(friendlyMessage(e));
    }
  }

  async function archiveCurrentWorkspace() {
    if (!workspaceId) return;
    setError(null);
    try {
      setWorkspace(await archiveWorkspace(getToken, workspaceId));
      await reloadWorkspaces();
    } catch (e) {
      setError(friendlyMessage(e));
    }
  }

  /**
   * Erases the workspace and drops back to the list. The detail screen must be torn down first —
   * leaving it mounted would keep refetching a workspace the server no longer has.
   */
  async function deleteCurrentWorkspace(): Promise<boolean> {
    if (!workspaceId) return false;
    setError(null);
    try {
      await deleteWorkspace(getToken, workspaceId);
      backToList();
      await reloadWorkspaces();
      return true;
    } catch (e) {
      setError(friendlyMessage(e));
      return false;
    }
  }

  async function changeMemberRole(userId: string, role: WorkspaceRole) {
    if (!workspaceId) return;
    setError(null);
    try {
      const updated = await updateWorkspaceMemberRole(getToken, workspaceId, userId, role);
      setMembers((cur) => cur.map((m) => (m.userId === userId ? updated : m)));
    } catch (e) {
      setError(friendlyMessage(e));
    }
  }

  async function removeMember(userId: string) {
    if (!workspaceId) return;
    setError(null);
    try {
      await removeWorkspaceMember(getToken, workspaceId, userId);
      setMembers((cur) => cur.filter((m) => m.userId !== userId));
    } catch (e) {
      setError(friendlyMessage(e));
    }
  }

  async function inviteMember(req: CreateInvitationRequest) {
    if (!workspaceId) return;
    setError(null);
    try {
      const inv = await createWorkspaceInvitation(getToken, workspaceId, req);
      setInvitations((cur) => [...cur, inv]);
    } catch (e) {
      setError(friendlyMessage(e));
    }
  }

  async function revokeInvitation(invitationId: string) {
    if (!workspaceId) return;
    setError(null);
    try {
      await revokeWorkspaceInvitation(getToken, workspaceId, invitationId);
      setInvitations((cur) => cur.filter((i) => i.id !== invitationId));
    } catch (e) {
      setError(friendlyMessage(e));
    }
  }

  async function addProject(req: CreateProjectRequest): Promise<Project | null> {
    if (!workspaceId) return null;
    setError(null);
    try {
      const proj = await createProject(getToken, workspaceId, req);
      setProjects((cur) => [...cur, proj]);
      return proj;
    } catch (e) {
      setError(friendlyMessage(e));
      return null;
    }
  }

  const reloadProjectDetail = useCallback(
    async (id: string) => {
      setLoadingProject(true);
      setError(null);
      try {
        const [p, mem] = await Promise.all([getProject(getToken, id), listProjectMembers(getToken, id)]);
        setProject(p);
        setProjectMembers(mem);
      } catch (e) {
        setError(friendlyMessage(e));
      } finally {
        setLoadingProject(false);
      }
    },
    [getToken],
  );

  function openProject(id: string) {
    setProjectId(id);
    setScreen("project");
    void reloadProjectDetail(id);
  }

  function backToWorkspace() {
    setScreen("workspace");
    setProjectId(null);
    setProject(null);
    if (workspaceId) void reloadWorkspaceDetail(workspaceId);
  }

  async function saveProject(req: UpdateProjectRequest) {
    if (!projectId) return;
    setError(null);
    try {
      setProject(await updateProject(getToken, projectId, req));
    } catch (e) {
      setError(friendlyMessage(e));
    }
  }

  async function archiveCurrentProject() {
    if (!projectId) return;
    setError(null);
    try {
      setProject(await archiveProject(getToken, projectId));
    } catch (e) {
      setError(friendlyMessage(e));
    }
  }

  async function addMemberToProject(userId: string, role: ProjectRole) {
    if (!projectId) return;
    setError(null);
    try {
      const pm = await addProjectMember(getToken, projectId, { userId, role });
      setProjectMembers((cur) => [...cur, pm]);
    } catch (e) {
      setError(friendlyMessage(e));
    }
  }

  async function changeProjectMemberRole(userId: string, role: ProjectRole) {
    if (!projectId) return;
    setError(null);
    try {
      const updated = await updateProjectMemberRole(getToken, projectId, userId, role);
      setProjectMembers((cur) => cur.map((m) => (m.userId === userId ? updated : m)));
    } catch (e) {
      setError(friendlyMessage(e));
    }
  }

  async function removeMemberFromProject(userId: string) {
    if (!projectId) return;
    setError(null);
    try {
      await removeProjectMember(getToken, projectId, userId);
      setProjectMembers((cur) => cur.filter((m) => m.userId !== userId));
    } catch (e) {
      setError(friendlyMessage(e));
    }
  }

  const reloadMyInvitations = useCallback(async () => {
    setLoadingMyInvitations(true);
    setError(null);
    try {
      setMyInvitations(await listMyInvitations(getToken));
    } catch (e) {
      setError(friendlyMessage(e));
    } finally {
      setLoadingMyInvitations(false);
    }
  }, [getToken]);

  async function acceptMyInvitation(id: string) {
    setError(null);
    try {
      await acceptInvitation(getToken, id);
      await reloadMyInvitations();
      await reloadWorkspaces();
    } catch (e) {
      setError(friendlyMessage(e));
    }
  }

  async function declineMyInvitation(id: string) {
    setError(null);
    try {
      await declineInvitation(getToken, id);
      await reloadMyInvitations();
    } catch (e) {
      setError(friendlyMessage(e));
    }
  }

  return {
    getToken,
    screen,
    error,
    clearError: () => setError(null),

    workspaces,
    loadingWorkspaces,
    addWorkspace,
    reloadWorkspaces,

    workspace,
    members,
    invitations,
    projects,
    loadingWorkspace,
    openWorkspace,
    backToList,
    saveWorkspace,
    archiveCurrentWorkspace,
    deleteCurrentWorkspace,
    changeMemberRole,
    removeMember,
    inviteMember,
    revokeInvitation,
    addProject,

    project,
    projectMembers,
    loadingProject,
    openProject,
    backToWorkspace,
    saveProject,
    archiveCurrentProject,
    addMemberToProject,
    changeProjectMemberRole,
    removeMemberFromProject,

    myInvitations,
    loadingMyInvitations,
    showMyInvitations,
    reloadMyInvitations,
    acceptMyInvitation,
    declineMyInvitation,
  };
}
